import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import {
  getFarthestExtentAlongAxis,
  getNearestExtentAlongAxis,
  normalizeVector,
  Point,
  Rect,
  toRect,
} from "@project/floorplanner/konva/geometry-konva";
import {
  FixedLabelPlacement,
  LabelPlacementMode,
  LabelPlacementResolver,
  LabelSide,
} from "@project/floorplanner/konva/label-placement";
import Konva from "konva";
import { Group } from "konva/lib/Group";

type LabelPlacementContext = {
  labelBounds: Rect;
  transform: Konva.Transform;
  origin: Point;
  labelOffsetX: number;
  labelOffsetY: number;
};

type RotatedPlacementPlan = {
  anchorLocalPoint: Point;
  localOutwardVector: Point;
  computeInitialPosition: (
    context: LabelPlacementContext,
    worldAnchorPoint: Point,
    worldOutwardVector: Point,
    gap: number,
  ) => { left: number; top: number };
};

/**
 * Centralizes label placement for all floorplan items.
 * Supports both labels that stay in a fixed position and labels that rotate together with their target shapes.
 */
export class LabelLayoutKonva {
  static placeLabelForItem(textGroup: Group, target: Konva.Shape | Konva.Group, item: FloorplanItem) {
    const profile = LabelPlacementResolver.resolveProfile(item);
    const scaledGap = this.getScaledGap(textGroup, profile.placement.gap);

    if (profile.mode === LabelPlacementMode.ROTATING) {
      this.placeRotatedLabel(textGroup, target, profile.placement.side, scaledGap);
      return;
    }

    this.placeFixedLabel(textGroup, target, { ...profile.placement, gap: scaledGap });
  }

  private static getScaledGap(textGroup: Group, baseGap: number) {
    const baseLabelHeight = (textGroup.getAttr("labelBaseHeight") as number | undefined) ?? 0;
    const labelBounds = textGroup.getClientRect({ relativeTo: textGroup.getParent() ?? undefined });
    const currentHeight = labelBounds.height;

    if (!textGroup.visible()) {
      return baseGap;
    }

    if (!baseLabelHeight && currentHeight > 0) {
      textGroup.setAttr("labelBaseHeight", currentHeight);
      return baseGap;
    }

    if (baseLabelHeight <= 0 || currentHeight <= 0) {
      return baseGap;
    }

    const scaleFactor = currentHeight / baseLabelHeight;
    return baseGap * scaleFactor;
  }

  private static placeRotatedLabel(textGroup: Group, target: Konva.Shape | Konva.Group, side: LabelSide, gap: number) {
    const targetBoundsLocal = toRect(target.getClientRect({ skipTransform: true }));
    const plan = this.getRotatedPlacementPlan(targetBoundsLocal, side);

    if (!plan) {
      return;
    }

    this.applyPlacementPlan(textGroup, target, targetBoundsLocal, gap, plan);
  }

  /**
   * Positions the label statically next to the target element.
   * The label is placed on the specified side and does not rotate with the target element.
   */
  private static placeFixedLabel(textGroup: Group, target: Konva.Shape | Konva.Group, placement: FixedLabelPlacement) {
    const parent = target.getParent() as Group | null;
    const targetBounds = toRect(target.getClientRect({ relativeTo: parent ?? undefined }));
    const labelMetrics = this.getLabelMetrics(textGroup, parent);
    const position = this.computeFixedLabelPlacement(targetBounds, labelMetrics.labelBounds, {
      ...placement,
      gap: placement.gap,
    });

    textGroup.x(position.left - labelMetrics.labelOffsetX);
    textGroup.y(position.top - labelMetrics.labelOffsetY);
  }

  /**
   * Positions the label next to a rotatable target element.
   * Calculates the initial position and pushes the label outwards if necessary to avoid overlap with the target element.
   */
  private static applyPlacementPlan(
    textGroup: Group,
    target: Konva.Shape | Konva.Group,
    targetBoundsLocal: Rect,
    gap: number,
    plan: RotatedPlacementPlan,
  ) {
    const context = this.getLabelLayoutContext(textGroup, target, targetBoundsLocal);
    const worldAnchorPoint = context.transform.point(plan.anchorLocalPoint);
    const worldOutwardVector = this.toWorldOutwardVector(context.transform, context.origin, plan.localOutwardVector);
    const { left, top } = plan.computeInitialPosition(context, worldAnchorPoint, worldOutwardVector, gap);

    textGroup.x(left - context.labelOffsetX);
    textGroup.y(top - context.labelOffsetY);

    this.pushLabelOutwardToAvoidOverlap(textGroup, target, targetBoundsLocal, plan.localOutwardVector, gap, context);
  }

  private static getRotatedPlacementPlan(targetBoundsLocal: Rect, side: LabelSide): RotatedPlacementPlan | null {
    switch (side) {
      case LabelSide.RIGHT:
      case LabelSide.LEFT:
        return this.createHorizontalPlacementPlan(targetBoundsLocal, side);
      case LabelSide.TOP:
      case LabelSide.BOTTOM:
        return this.createVerticalPlacementPlan(targetBoundsLocal, side);
      default:
        return null;
    }
  }

  private static getLabelMetrics(textGroup: Group, parent: Group | null) {
    const labelBounds = toRect(textGroup.getClientRect({ relativeTo: parent ?? undefined }));
    const labelOffsetX = labelBounds.x - textGroup.x();
    const labelOffsetY = labelBounds.y - textGroup.y();

    return { labelBounds, labelOffsetX, labelOffsetY };
  }

  private static computeFixedLabelPlacement(targetBounds: Rect, labelBounds: Rect, placement: FixedLabelPlacement) {
    const { side, gap, offsetX = 0, offsetY = 0 } = placement;
    let left = targetBounds.x;
    let top = targetBounds.y;
    const alignBottom = targetBounds.y + targetBounds.height - labelBounds.height;
    const alignRight = targetBounds.x + targetBounds.width - labelBounds.width;

    if (side === LabelSide.RIGHT || side === LabelSide.LEFT) {
      left = side === LabelSide.RIGHT ? targetBounds.x + targetBounds.width + gap : targetBounds.x - gap - labelBounds.width;
      top = alignBottom;
    } else {
      top = side === LabelSide.BOTTOM ? targetBounds.y + targetBounds.height + gap : targetBounds.y - gap - labelBounds.height;
      left = alignRight;
    }

    return { left: left + offsetX, top: top + offsetY };
  }

  private static getLabelLayoutContext(
    textGroup: Group,
    target: Konva.Shape | Konva.Group,
    targetBoundsLocal: Rect,
  ): LabelPlacementContext {
    const parent = target.getParent() as Group | null;
    const { labelBounds, labelOffsetX, labelOffsetY } = this.getLabelMetrics(textGroup, parent);
    const transform = target.getTransform();
    const origin = { x: targetBoundsLocal.x, y: targetBoundsLocal.y };

    return { labelBounds, transform, origin, labelOffsetX, labelOffsetY };
  }

  private static toWorldOutwardVector(transform: Konva.Transform, origin: Point, localOutwardVector: Point) {
    const worldOriginPoint = transform.point(origin);
    const worldOutPoint = transform.point({ x: origin.x + localOutwardVector.x, y: origin.y + localOutwardVector.y });

    return normalizeVector({
      x: worldOutPoint.x - worldOriginPoint.x,
      y: worldOutPoint.y - worldOriginPoint.y,
    });
  }

  private static createHorizontalPlacementPlan(
    targetBoundsLocal: Rect,
    placement: LabelSide.RIGHT | LabelSide.LEFT,
  ): RotatedPlacementPlan {
    const isRightSide = placement === LabelSide.RIGHT;
    const anchorLocalPoint = isRightSide
      ? { x: targetBoundsLocal.x + targetBoundsLocal.width, y: targetBoundsLocal.y + targetBoundsLocal.height }
      : { x: targetBoundsLocal.x, y: targetBoundsLocal.y + targetBoundsLocal.height };
    const localOutwardVector: Point = isRightSide ? { x: 1, y: 0 } : { x: -1, y: 0 };

    return {
      anchorLocalPoint,
      localOutwardVector,
      computeInitialPosition: (context, worldAnchorPoint, worldOutwardVector, gap) => {
        const pointsLeft = worldOutwardVector.x < 0;
        const left = worldAnchorPoint.x + worldOutwardVector.x * gap + (pointsLeft ? -context.labelBounds.width : 0);
        const top = worldAnchorPoint.y + worldOutwardVector.y * gap - context.labelBounds.height;
        return { left, top };
      },
    };
  }

  private static createVerticalPlacementPlan(
    targetBoundsLocal: Rect,
    placement: LabelSide.TOP | LabelSide.BOTTOM,
  ): RotatedPlacementPlan {
    const anchorLocalPoint: Point = {
      x: targetBoundsLocal.x + targetBoundsLocal.width / 2,
      y: placement === LabelSide.TOP ? targetBoundsLocal.y : targetBoundsLocal.y + targetBoundsLocal.height,
    };
    const localOutwardVector: Point = placement === LabelSide.TOP ? { x: 0, y: -1 } : { x: 0, y: 1 };

    return {
      anchorLocalPoint,
      localOutwardVector,
      computeInitialPosition: (context, worldAnchorPoint, _worldOutwardVector, gap) => {
        const left = worldAnchorPoint.x - context.labelBounds.width / 2;
        const top =
          placement === LabelSide.TOP ? worldAnchorPoint.y - context.labelBounds.height - gap : worldAnchorPoint.y + gap;
        return { left, top };
      },
    };
  }

  private static pushLabelOutwardToAvoidOverlap(
    textGroup: Group,
    target: Konva.Shape | Konva.Group,
    targetBoundsLocal: Rect,
    localOutwardVector: Point,
    gap: number,
    context?: LabelPlacementContext,
  ) {
    const layoutContext = context ?? this.getLabelLayoutContext(textGroup, target, targetBoundsLocal);
    const parent = target.getParent() as Group | null;
    const labelBounds = toRect(textGroup.getClientRect({ relativeTo: parent ?? undefined }));
    const { transform, origin } = layoutContext;
    const worldOutwardVector = this.toWorldOutwardVector(transform, origin, localOutwardVector);
    const targetFrontEdge = getFarthestExtentAlongAxis(targetBoundsLocal, transform, worldOutwardVector);
    const labelBackEdge = getNearestExtentAlongAxis(labelBounds, worldOutwardVector);
    const requiredPushDistance = targetFrontEdge + gap - labelBackEdge;

    if (requiredPushDistance > 0) {
      textGroup.x(textGroup.x() + worldOutwardVector.x * requiredPushDistance);
      textGroup.y(textGroup.y() + worldOutwardVector.y * requiredPushDistance);
    }
  }
}
